import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useConvoStore } from 'src/stores/conversations';
import { of, iif, zip } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap, mergeMap } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { ChatMiscDataPlugin } from 'src/features/messaging/chat/components/methods/misc';
import { StandardAlertsPlugin } from 'src/plugins/alert/alertPlugin';
/**
 * Plugin containing method to update
 * if current user is typing in the firestore
 */
const ChatQueryPlugin = (backFunctionCb) => {
    const convoStore = useConvoStore();
    const chatLoading = computed(() => convoStore.chatLoading);
    const { user, convo, liveConvo, receiver } = ChatMiscDataPlugin();
    const { errorAlert } = StandardAlertsPlugin();
    const { subject: startQuery$ } = rxInitSubjectCallback();
    const { subject: getLastestMsg$ } = rxInitSubjectCallback();
    const { subject: query$ } = rxInitSubjectCallback();
    const { subject: chatErrorHandler$ } = rxInitSubjectCallback();
    const { subject: readMessage$ } = rxInitSubjectCallback();
    /**
     * Get Chatroom data
     */
    useObservable(startQuery$.pipe(filter(() => !chatLoading.value), mergeMap(() => iif(() => (!(convo.value.id in liveConvo.value)), of('').pipe(switchMap(() => {
        return import(
        /* webpackChunkName: "firebaseMethod" */
        'src/_helpers/firebaseMethods');
    }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
        return zip(firebaseMethods(), import(
        /* webpackChunkName: "firebaseMethod" */
        'firebase/firestore'));
    }), switchMap(([{ db }, { doc: fbDoc, getDoc }]) => {
        return getDoc(fbDoc(db, 'chat', convo.value.id));
    }), tap(conversation => {
        if (!(convo.value.id in liveConvo.value)) {
            convoStore.parseChatRoom({
                data: conversation.data(),
                roomID: convo.value.id
            });
        }
    }), map(() => true)), of(''))), tap(() => {
        getLastestMsg$.next('');
    }), tap(() => {
        query$.next('');
    })));
    /**
     * Handle error when retrieving chat msgs
     */
    useObservable(chatErrorHandler$.pipe(tap(errCode => {
        if (errCode !== 'permission-denied')
            return;
        errorAlert('Chat room not found. Please try again');
        backFunctionCb();
    })));
    /**
     * Get latest message
     */
    const lastMsg = ref(null);
    const replied = ref(false);
    useObservable(getLastestMsg$.pipe(
    // filter(() => !process.env.SERVER),
    switchMap(() => {
        return import(/* webpackChunkName: "firebaseMethod" */ 'src/_helpers/firebaseMethods');
    }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
        return zip(firebaseMethods(), import(
        /* webpackChunkName: "firebaseMethod" */
        'firebase/firestore'), import(
        /* webpackChunkName: "firebaseRx" */
        'rxfire/firestore'));
    }), switchMap(([{ db }, { collection: fbCollection, query, orderBy, limit, Timestamp }, { collection }]) => {
        return collection(query(fbCollection(db, `${'chat'}/${convo.value.id}/messages`), orderBy('createdAt', 'desc'), limit(1))).pipe(catchError((err) => {
            chatErrorHandler$.next(err.code);
            return of('');
        }), filter(v => v !== ''), map((docs) => docs), tap((docs) => {
            // parse msg
            docs.forEach(item => {
                const message = item.data();
                // indicate final message
                lastMsg.value = message;
                // check if we replied the last msg
                if (Number(message.sender) === Number(user.value.user_pk)) {
                    replied.value = true;
                }
                // if createdAt is not available, set it to now
                if (!message.createdAt) {
                    message.createdAt = Timestamp.fromDate(new Date());
                }
                convoStore.addMessagesToList({
                    conversationId: convo.value.id,
                    message: {
                        ...message,
                        id: item.id
                    }
                });
            });
        }));
    }), tap(() => {
        readMessage$.next('');
    })));
    /**
     * Get next messages up to the limit
     */
    const msgLimit = 50;
    const initialized = ref(false);
    const loading = ref(false);
    const lastMsgId = ref(null);
    const noPrevMsgs = ref(false);
    const currPage = ref(null);
    useObservable(query$.pipe(
    // filter(() => !process.env.SERVER),
    filter(() => !chatLoading.value), switchMap(startAfter => {
        return of(startAfter).pipe(filter(() => convo.value.id !== null), switchMap(() => {
            return import(
            /* webpackChunkName: "firebaseMethod" */
            'src/_helpers/firebaseMethods');
        }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
            return zip(firebaseMethods(), import(
            /* webpackChunkName: "firebaseMethod" */
            'firebase/firestore'));
        }), switchMap(([{ db }, { collection: fbCollection, startAfter: fbstartAfter, getDocs, query, orderBy, limit, }]) => {
            return of('').pipe(map(() => {
                const convoRef = fbCollection(db, `${'chat'}/${convo.value.id}/messages`);
                if (startAfter) {
                    return query(convoRef, orderBy('createdAt', 'desc'), fbstartAfter(startAfter), limit(msgLimit));
                }
                return query(convoRef, orderBy('createdAt', 'desc'), limit(msgLimit));
            }), filter(v => v !== null), tap(() => {
                loading.value = true;
            }), switchMap(query => {
                return of('').pipe(switchMap(() => {
                    return getDocs(query);
                }), catchError((err) => {
                    chatErrorHandler$.next(err.code);
                    return of('');
                }));
            }));
        }), 
        //@ts-expect-error ignore
        filter(v => v !== ''), tap(({ docs }) => {
            if (docs.length === 0)
                lastMsgId.value = null;
            if (docs.length < msgLimit)
                noPrevMsgs.value = true;
            currPage.value = docs[docs.length - 1];
            docs.forEach(item => {
                const message = item.data();
                convoStore.addMessagesToList({
                    conversationId: convo.value.id,
                    message: {
                        ...message,
                        id: item.id
                    }
                });
            });
        }), tap(() => {
            loading.value = false;
        }), debounceTime(300), tap(() => {
            initialized.value = true;
        }));
    })));
    /**
     * Update read status
     * of all messages in the chat room
     */
    useObservable(readMessage$.pipe(
    // filter(() => !process.env.SERVER),
    debounceTime(200), switchMap(() => {
        return import(
        /* webpackChunkName: "firebaseMethod" */
        'src/_helpers/firebaseMethods');
    }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
        return zip(firebaseMethods(), import(
        /* webpackChunkName: "firebaseMethod" */
        'firebase/firestore'));
    }), switchMap(([{ db }, { collection: fbCollection, doc: fbDoc, writeBatch, query, where, getDocs, updateDoc, increment: fbIncrement }]) => {
        let batch = writeBatch(db);
        const convoRef = fbDoc(db, 'chat', convo.value.id);
        const isoDate = new Date().toISOString();
        const userID = user.value.user_pk;
        /**
         * Update chatroom data
         */
        batch.update(convoRef, {
            [`last_active_${userID}`]: isoDate,
            [`unread_${userID}`]: 0,
            updated: isoDate
        });
        return of('').pipe(switchMap(() => {
            /**
             * Query for unread chats
             */
            return getDocs(query(fbCollection(db, `chat/${convo.value.id}/messages`), where('sender', '==', Number(receiver.value)), where('read', '==', '')));
        }), tap(querySnapshot => {
            if (querySnapshot.empty) {
                // if empty, save data
                void batch.commit();
                return;
            }
            /**
             * Update unread count in notification store
             * minusing the count of unread messages
             */
            void updateDoc(fbDoc(db, 'notification', `${userID}`), {
                unread_chat: fbIncrement(Number(-querySnapshot.size)),
                updated: isoDate
            });
        }), filter(querySnapshot => !querySnapshot.empty), tap(querySnapshot => {
            /**
             * Update read status for all chat messages
             */
            let batchcount = 0;
            querySnapshot.forEach((doc) => {
                batch.update(fbDoc(db, 'chat', `${convo.value.id}/messages/${doc.id}`), { read: isoDate, updated: isoDate });
                batchcount += 1;
                // commit per 18 messages so as to not overload
                // the transaction batch
                if (batchcount === 18) {
                    void batch.commit();
                    batchcount = 0;
                    batch = writeBatch(db);
                }
            });
            // commit remaining
            void batch.commit();
        }));
    })));
    /**
     * Set user chat room status to active
     */
    const { subject: setStatus$ } = rxInitSubjectCallback();
    useObservable(setStatus$.pipe(
    // filter(() => !process.env.SERVER),
    filter(() => !chatLoading.value), switchMap(status => {
        return of('').pipe(switchMap(() => {
            return import(
            /* webpackChunkName: "firebaseMethod" */
            'src/_helpers/firebaseMethods');
        }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
            return zip(firebaseMethods(), import(
            /* webpackChunkName: "firebaseMethod" */
            'firebase/firestore'));
        }), filter(() => convo.value.id !== null), switchMap(([{ db }, { doc: fbDoc, updateDoc }]) => {
            return updateDoc(fbDoc(db, 'chat', convo.value.id), {
                [`is_active_${user.value.user_pk}`]: status
            });
        }), catchError((err) => {
            chatErrorHandler$.next(err.code);
            return of('');
        }));
    })));
    /**
     * Decide when to start query
     */
    const chatMounted = ref(false);
    onMounted(() => {
        chatMounted.value = true;
        if (chatLoading.value)
            return;
        startQuery$.next('');
        setStatus$.next(true);
    });
    onBeforeUnmount(() => {
        if (!convo.value.id)
            return;
        setStatus$.next(false);
        void convoStore.clearConversation('chat', user.value?.user_pk.toString() || '');
    });
    watch(chatLoading, (newValue) => {
        if (newValue || !chatMounted.value)
            return;
        startQuery$.next('');
    });
    watch(convo, (newValue, oldValue) => {
        if ((!oldValue) ||
            ((newValue && oldValue) &&
                (newValue.id !== oldValue.id))) {
            // when convo is changed, requery
            void convoStore.clearConversation('chat', user.value?.user_pk.toString() || '');
            initialized.value = false;
            if (!chatMounted.value)
                return;
            startQuery$.next('');
        }
    }, { immediate: true });
    return {
        lastMsg,
        replied,
        initialized,
        loading,
        lastMsgId,
        noPrevMsgs,
        currPage,
        query$
    };
};
export { ChatQueryPlugin };
